import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FieldWrapper } from './FieldWrapper';
import { TypeaheadInput } from './SelectTypeaheadField';
import { Icon } from '../Icon';
import { parseRenderOptions } from './utils';
import { is, isImmutable, List, Map } from 'immutable';
import classNames from 'classnames';
import { debounce } from 'lodash-es';
import { CodeEditor } from '../code_editor/CodeEditor';

function usePrevious(value) {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef();
  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current;
}

const newListRecord = Map({ key: '', value: '', children: List() });

/*
 * Converts the value Map to a values List.
 *
 * The actual value stored for this field is a Map, but in order to allow
 * duplicate keys in the UI (such as when a key of 'ant' exists and the user
 * wants to type 'antenna', causing a temporary duplicate), we want to work
 * with the data as a list. This function will take the Map value, and
 * transform it into a nested list.
 *
 * Expected Map input:
 * {
 *   keyOne: { value: 'valueOne' },
 *   keyTwo: {
 *     value: 'valueTwo',
 *     children: {
 *       subKeyA: 'subValueA',
 *       subKeyB: 'subValueB',
 *     }
 *   },
 * }
 *
 * Expected List output:
 * [
 *   { key: 'keyOne', value: 'valueOne', children: [] },
 *   {
 *     key: 'keyTwo',
 *     value: 'valueTwo',
 *     children: [
 *       { key: 'subKeyA', value: 'subValueA', children: [] },
 *       { key: 'subKeyB', value: 'subValueB', children: [] },
 *     ]
 *   },
 * ]
 */
const reduceMapToList = (
  map,
  // Options for the property names of the value and children
  { valueProp = 'value', childrenProp = 'children' } = {},
  nested = false,
) =>
  map
    ? map
        .reduceRight(
          (values, value, key) =>
            values.unshift(
              Map.isMap(value)
                ? // If the value is a map, pull out the value and children from it
                  Map({
                    key,
                    value: value.get(valueProp),
                    // Map children recursively
                    children: reduceMapToList(
                      value.get(childrenProp),
                      { valueProp, childrenProp },
                      true,
                    ),
                  })
                : // Otherwise, set the value as is if it's a string
                  newListRecord.merge({
                    key,
                    value: typeof value === 'string' ? value : '',
                  }),
            ),
          List(),
        )
        .update(list =>
          !nested && list.isEmpty()
            ? list.push(Map({ key: '', value: '', children: List() }))
            : list,
        )
    : !nested
      ? List([Map({ key: '', value: '', children: List() })])
      : List();

/*
 * Converts the values List to a value Map.
 *
 * Since the field uses a List as its source data, we need to transform it back
 * to a Map when we save the value into the form. This function does the
 * reverse transformation of the `reduceMapToList` function above.
 */
const reduceListToMap = (
  list,
  // Options for the property names of the value and children
  { valueProp = 'value', childrenProp = 'children' } = {},
  // Is this the lest nested level, where the values are just the string values
  // instead of objects
  lastLevel = false,
) =>
  list
    .filter(
      (item, index, list) =>
        !!item.get('key') &&
        !list.take(index).some(r => r.get('key') === item.get('key')),
    )
    .reduce(
      (vals, row) =>
        vals.set(
          row.get('key'),
          lastLevel
            ? row.get('value')
            : row.get('children')?.size > 0
              ? Map({
                  [valueProp]: row.get('value'),
                  [childrenProp]: reduceListToMap(
                    row.get('children'),
                    { valueProp, childrenProp },
                    // Set lastLevel to true to only allow one level of nesting
                    true,
                  ),
                })
              : Map({ [valueProp]: row.get('value') }),
        ),
      Map(),
    );

// Validates a row and returns an error message if the key is empty or if the
// key has been used previously in the list.
const validateRow = (
  row,
  index,
  values,
  { pattern, patternMessage = 'Invalid key format.' } = {},
) =>
  !row.get('key')
    ? !row.get('value')
      ? null
      : 'Key cannot be empty. This record will be discarded.'
    : pattern && !row.get('key').match(pattern)
      ? patternMessage
      : values.take(index).some(r => r.get('key') === row.get('key'))
        ? 'Key cannot be a duplicate. This record will be discarded.'
        : null;

export const NestedMapField = props => {
  const {
    onChange: onChangeOrig,
    onBlur: onBlurOrig,
    addOnBlur,
    onFocus: onFocusOrig,
    addOnDelete,
    value,
    codeBindings,
  } = props;

  const renderOptions = parseRenderOptions(props);

  // Use a typeahead field for the key if options are provided, or if the
  // typeahead render attribute is true
  const useTypeahead =
    !props.options.isEmpty() || !!renderOptions.custom.typeahead;
  // Define the available options as those that have not yet been selected
  const availableOptions =
    useTypeahead &&
    props.options.filter(option => !value.has(option.get('value')));

  // Use a code editor for the value if the code renderAttribute is an object
  const useCodeEditor = isImmutable(renderOptions.custom.code);

  // Get the name/label to use in the add and delete buttons
  const itemName = renderOptions.custom.itemName || 'Row';

  // Get the property names for parsing the value object
  const valueProp = renderOptions.custom.valuePropertyName;
  const childrenProp = renderOptions.custom.childrenPropertyName;

  // Convert the map value to a list so we can show errors for duplicate keys
  // instead of overwriting data. Adds an empty record to the end of the list.
  const [values, setValues] = useState(
    reduceMapToList(value, { valueProp, childrenProp }),
  );

  // Store the previous field value so we can tell when it changes
  const previousValue = usePrevious(value);
  // Check if the value changed outside of this component, and if it has,
  // update the local list state to match the new value
  useEffect(() => {
    if (
      !is(value, previousValue) &&
      !is(Map(value), reduceListToMap(values, { valueProp, childrenProp }))
    ) {
      setValues(reduceMapToList(value, { valueProp, childrenProp }));
    }
  }, [value, previousValue, values, valueProp, childrenProp]);

  // Change event for the key of a row that updates the values state and calls
  // the original onChange function with the new field value
  const onChangeKey = useCallback(
    indexArray => key => {
      let newValues = values.setIn(
        [...indexArray.flatMap(i => [i, 'children']).slice(0, -1), 'key'],
        key,
      );
      setValues(newValues);
      onChangeOrig(reduceListToMap(newValues, { valueProp, childrenProp }));
    },
    [onChangeOrig, values, valueProp, childrenProp],
  );

  // Change event for the value of a row that updates the values state and calls
  // the original onChange function with the new field value
  const onChangeValue = useCallback(
    indexArray => value => {
      const newValues = values.setIn(
        [...indexArray.flatMap(i => [i, 'children']).slice(0, -1), 'value'],
        value,
      );
      setValues(newValues);
      onChangeOrig(reduceListToMap(newValues, { valueProp, childrenProp }));
    },
    [onChangeOrig, values, valueProp, childrenProp],
  );

  // Change event for deleting a row that updates the values state and calls
  // the original onChange function with the new field value
  const onDelete = useCallback(
    indexArray =>
      (...args) => {
        const newValues = values.deleteIn(
          indexArray.flatMap(i => [i, 'children']).slice(0, -1),
        );
        setValues(newValues);
        onChangeOrig(reduceListToMap(newValues, { valueProp, childrenProp }));
        if (typeof addOnDelete === 'function') addOnDelete(...args);
      },
    [onChangeOrig, values, valueProp, childrenProp, addOnDelete],
  );

  // Change event for adding a row that inserts an empty row either into the
  // root, or nested if parentIndex is provided
  const onAdd = useCallback(
    parentIndex => () => {
      const newValues =
        typeof parentIndex === 'number'
          ? values.updateIn([parentIndex, 'children'], children =>
              children.push(newListRecord),
            )
          : values.push(newListRecord);
      setValues(newValues);
      onChangeOrig(reduceListToMap(newValues, { valueProp, childrenProp }));
    },
    [onChangeOrig, values, valueProp, childrenProp],
  );

  const KeyField = useCallback(
    fieldProps =>
      useTypeahead ? (
        <TypeaheadInput
          componentOptions={{
            size: fieldProps.size,
            fieldClassName: fieldProps.fieldClassName,
          }}
          id={fieldProps.id}
          disabled={fieldProps.disabled}
          onChange={fieldProps.onChange}
          onFocus={fieldProps.onFocus}
          onBlur={fieldProps.onBlur}
          value={fieldProps.value}
          options={fieldProps.options}
          allowNew={fieldProps.allowNew}
          placeholder={fieldProps.placeholder}
          form={fieldProps.form}
        />
      ) : (
        <input
          className={classNames('form-control', fieldProps.fieldClassName, {
            'form-control-sm': fieldProps.size === 'sm',
            'is-invalid': !!fieldProps.error,
          })}
          type="text"
          id={fieldProps.id}
          name={fieldProps.name}
          disabled={fieldProps.disabled}
          onChange={e => fieldProps.onChange(e.target.value)}
          onFocus={fieldProps.onFocus}
          onBlur={fieldProps.onBlur}
          value={fieldProps.value}
          placeholder={fieldProps.placeholder}
          form={fieldProps.form}
          autoComplete="false"
        />
      ),
    [useTypeahead],
  );

  const ValueField = useCallback(
    fieldProps =>
      useCodeEditor ? (
        <CodeEditor
          simple
          title={fieldProps.code.title}
          language={fieldProps.code.language}
          className={fieldProps.fieldClassName}
          wrapperClassName="code-editor--max-sm"
          id={fieldProps.id}
          onChange={fieldProps.onChange}
          onFocus={fieldProps.onFocus}
          onBlur={fieldProps.onBlur}
          value={fieldProps.value}
          bindings={fieldProps.code.bindings}
          placeholder={fieldProps.placeholder}
          disabled={fieldProps.disabled}
          allowCopy={fieldProps.code.allowCopy}
        />
      ) : (
        <input
          className={classNames('form-control', fieldProps.fieldClassName, {
            'form-control-sm': fieldProps.size === 'sm',
          })}
          type="text"
          id={fieldProps.id}
          name={fieldProps.name}
          disabled={fieldProps.disabled}
          onChange={e => fieldProps.onChange(e.target.value)}
          onFocus={fieldProps.onFocus}
          onBlur={fieldProps.onBlur}
          value={fieldProps.value}
          placeholder={fieldProps.placeholder}
          form={fieldProps.form}
        />
      ),
    [useCodeEditor],
  );

  // Debounce the focus and blur events because this component consists of
  // multiple inputs, and we want to make sure that moving focus from one to
  // another doesn't trigger an event
  const debouncedSetFocused = useMemo(
    () =>
      debounce((isFocused, ...args) => {
        if (isFocused) {
          if (typeof onFocusOrig === 'function') onFocusOrig(...args);
        } else {
          if (typeof onBlurOrig === 'function') onBlurOrig(...args);
          if (typeof addOnBlur === 'function') addOnBlur(...args);
        }
      }, 50),
    [onFocusOrig, onBlurOrig, addOnBlur],
  );
  // Delay focus function because the CodeField doesn't emit its events as
  // quickly as a native field, so moving from a CodeField to an input triggers
  // the events in a different order than the reverse
  const handleFocus = useCallback(
    (...args) => {
      window.setTimeout(() => debouncedSetFocused(true, ...args), 10);
    },
    [debouncedSetFocused],
  );
  const handleBlur = useCallback(
    (...args) => debouncedSetFocused(false, ...args),
    [debouncedSetFocused],
  );

  const codeEditorBindings =
    codeBindings?.root || renderOptions.custom.code.get('options');

  return (
    <FieldWrapper {...props} renderOptions={renderOptions} fieldset>
      {values.map((row, index) => {
        // Check if this row has an error
        const error = validateRow(row, index, values, {
          pattern: renderOptions.custom.keyPattern,
          patternMessage: renderOptions.custom.keyPatternMessage,
        });

        // Get the children of this row
        const children = row.get('children');

        const nestedCodeEditorBindings =
          codeBindings?.nested ||
          renderOptions.custom.code.get('nestedOptions');

        // Render a row for each value
        return (
          // Only render the last row if applicable per the above flag
          <div
            className={classNames('input-group__wrapper', {
              'no-children': children.size === 0,
            })}
            key={`row-${index}`}
          >
            <div
              className={classNames('input-group input-group--map-field', {
                'input-group-sm': renderOptions.size === 'sm',
              })}
            >
              <KeyField
                id={`${props.id}-${index}-key`}
                name={`${props.name}-${index}-key`}
                disabled={!props.enabled}
                value={row.get('key')}
                onChange={onChangeKey([index])}
                onFocus={handleFocus}
                onBlur={handleBlur}
                size={renderOptions.size}
                fieldClassName={renderOptions.fieldClassName}
                allowNew={renderOptions.custom.allowNew}
                placeholder={renderOptions.placeholder}
                form={props.form}
                options={availableOptions}
                error={error}
              />
              <ValueField
                id={`${props.id}-${index}-value`}
                name={`${props.name}-${index}-value`}
                disabled={!props.enabled}
                value={row.get('value')}
                onChange={onChangeValue([index])}
                onFocus={handleFocus}
                onBlur={handleBlur}
                size={renderOptions.size}
                fieldClassName={renderOptions.fieldClassName}
                placeholder={renderOptions.custom.valuePlaceholder}
                form={props.form}
                code={
                  useCodeEditor
                    ? {
                        title: `${props.label}${
                          !!props.label && !!row.get('key') ? ': ' : ''
                        }${row.get('key')}`,
                        language: renderOptions.custom.code.get('language'),
                        bindings: isImmutable(codeEditorBindings)
                          ? codeEditorBindings.toJS()
                          : codeEditorBindings,
                        allowCopy: renderOptions.custom.code.get('allowCopy'),
                      }
                    : {}
                }
              />
              {props.enabled && (
                <button
                  className="btn btn-table"
                  type="button"
                  onClick={onDelete([index])}
                  aria-label={`Delete ${itemName}`}
                  title={`Delete ${itemName}`}
                >
                  <Icon name="trash" md />
                </button>
              )}
            </div>
            {error && <div className="form-text text-danger">{error}</div>}

            {/* Render nested children for current row */}
            {children.size > 0 && (
              <div
                className="input-group__children"
                key={`row-${index}-children`}
              >
                {children.map((child, childIndex) => {
                  // Check if this row has an error
                  const childError = validateRow(child, childIndex, children, {
                    pattern: renderOptions.custom.keyPattern,
                    patternMessage: renderOptions.custom.keyPatternMessage,
                  });

                  // Render a row for each value
                  return (
                    // Only render the last row if applicable per the above flag
                    <React.Fragment key={`row-${index}.${childIndex}`}>
                      <div
                        className={classNames(
                          'input-group input-group--map-field',
                          {
                            'input-group-sm': renderOptions.size === 'sm',
                          },
                        )}
                      >
                        <KeyField
                          id={`${props.id}-${index}_${childIndex}-key`}
                          name={`${props.name}-${index}_${childIndex}-key`}
                          disabled={!props.enabled}
                          value={child.get('key')}
                          onChange={onChangeKey([index, childIndex])}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                          size={renderOptions.size}
                          fieldClassName={renderOptions.fieldClassName}
                          allowNew={renderOptions.custom.allowNew}
                          placeholder={renderOptions.placeholder}
                          form={props.form}
                          options={availableOptions}
                          error={error}
                        />
                        <ValueField
                          id={`${props.id}-${index}_${childIndex}-value`}
                          name={`${props.name}-${index}_${childIndex}-value`}
                          disabled={!props.enabled}
                          value={child.get('value')}
                          onChange={onChangeValue([index, childIndex])}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                          size={renderOptions.size}
                          fieldClassName={renderOptions.fieldClassName}
                          placeholder={renderOptions.custom.valuePlaceholder}
                          form={props.form}
                          code={
                            useCodeEditor
                              ? {
                                  title: `${props.label}${
                                    !!props.label && !!row.get('key')
                                      ? ': '
                                      : ''
                                  }${row.get('key')}${
                                    !!row.get('key') && !!child.get('key')
                                      ? ' > '
                                      : ''
                                  }${child.get('key')}`,
                                  language:
                                    renderOptions.custom.code.get('language'),
                                  bindings: isImmutable(
                                    nestedCodeEditorBindings,
                                  )
                                    ? nestedCodeEditorBindings.toJS()
                                    : nestedCodeEditorBindings,
                                  allowCopy:
                                    renderOptions.custom.code.get('allowCopy'),
                                }
                              : {}
                          }
                        />
                        {props.enabled && (
                          <button
                            className="btn btn-table"
                            type="button"
                            onClick={onDelete([index, childIndex])}
                            aria-label={`Delete Nested ${itemName}`}
                            title={`Delete Nested ${itemName}`}
                          >
                            <Icon name="trash" md />
                          </button>
                        )}
                      </div>
                      {childError && (
                        <div className="form-text text-danger">
                          {childError}
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
            {props.enabled && (
              <div className="input-group__action">
                <Icon name="plus-circle" md className="text-primary me-1" />
                <span>
                  Add a{' '}
                  <button
                    className="btn btn-text"
                    type="button"
                    onClick={onAdd(index)}
                    aria-label={`Add Nested ${itemName}`}
                  >
                    nested {itemName.toLowerCase()}
                  </button>
                </span>
              </div>
            )}
          </div>
        );
      })}
      {values.size === 0 && (
        <div className="py-2 no-data bg-white text-start">
          <em className="text-muted">There are no defined outputs.</em>
        </div>
      )}
      {props.enabled && (
        <div className="py-2">
          <Icon name="plus-circle" md className="text-primary me-1" />
          <span>
            Add a{' '}
            <button
              className="btn btn-text"
              type="button"
              onClick={onAdd()}
              aria-label={`Add ${itemName}`}
            >
              new {itemName.toLowerCase()}
            </button>
          </span>
        </div>
      )}
    </FieldWrapper>
  );
};
